import { useCallback, useEffect, useState } from 'react';

import { AutoComplete } from 'primereact/autocomplete';

import { useLazySearchContactsQuery } from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { Contact } from '@/rtk-query.gql';

import { ContactAutoCompleteProps, FormattedContact } from './types';

const formatContact = (contact: Contact): FormattedContact =>
  Object.assign({}, contact, {
    displayName: `${contact.fullName || ''}${contact.company ? ` [${contact.company}]` : ''}`,
  });

export const ContactAutoComplete = ({
  className,
  label,
  defaultValue,
  defaultId,
  appendTo = 'self',
  ...autoCompleteProps
}: ContactAutoCompleteProps) => {
  const [filtered, setFiltered] = useState<FormattedContact[]>([]);
  const [inputValue, setInputValue] = useState(defaultValue?.fullName);
  const [selected, setSelected] = useState<FormattedContact | undefined>(
    defaultValue && defaultValue.fullName
      ? formatContact(defaultValue)
      : undefined,
  );

  const [query] = useLazySearchContactsQuery();

  useEffect(() => {
    if (defaultValue) {
      if (setInputValue) {
        setInputValue(defaultValue?.fullName as string);
      }
    } else if (defaultValue === null) {
      setInputValue('');
    } else if (defaultId) {
      query({
        search: defaultId,
        selectionLimit: SELECTION_LIMIT,
      }).then(({ data = [] }) => {
        const contact = data.find((cont) => cont.id === defaultId);
        if (contact) {
          const formattedContact = formatContact(contact);
          setSelected(formattedContact);
          setInputValue(formattedContact.displayName);
        }
      });
    }
  }, [defaultValue, defaultId, query]);

  const search = useCallback(
    async (event: { query: string }) => {
      const { data = [] } = await query({
        search: event.query || '',
        selectionLimit: SELECTION_LIMIT,
      });

      setFiltered(data.map((contact) => formatContact(contact)));
    },
    [query],
  );

  const handleSelect = (e: { value: FormattedContact }) => {
    setSelected(e.value);
    if (setInputValue) {
      setInputValue(e.value?.displayName as string);
    }
  };

  const handleChange = (e: { value: string | FormattedContact }) => {
    if (setInputValue) {
      const value =
        typeof e.value === 'object' && e.value !== null
          ? e.value.displayName
          : (e.value as string);
      setInputValue(value as string);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className || ''}`}>
      {label && (
        <label className="font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <AutoComplete
        appendTo={appendTo}
        suggestions={filtered}
        field="displayName"
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
        className={`w-full ${className || ''}`}
      />
    </div>
  );
};
